var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useEffect, useLayoutEffect, useMemo, useState, } from "react";
import { createPortal } from "react-dom";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { ModalProvider, ModalRoot, StyleBaseline, TooltipProvider, TooltipRoot, } from "@animech/ui";
import * as Styled from "./popout-window.style";
import { GlobalStyle, ZIndex } from "ui/app/global.style";
/**
 * Opens a blank same-origin window for `PopoutWindow` to render into. Must be
 * called straight from a user gesture (a click handler), or the browser's
 * popup blocker swallows it. Returns null when the window was blocked.
 */
export var openPopoutWindow = function (_a) {
    var name = _a.name, title = _a.title, width = _a.width, height = _a.height;
    var popup = window.open("", name, "popup,width=".concat(Math.round(width), ",height=").concat(Math.round(height)));
    if (!popup)
        return null;
    var popupDocument = popup.document;
    // A window reused by name still holds what an earlier popout left in it.
    popupDocument.head.replaceChildren();
    popupDocument.body.replaceChildren();
    popupDocument.title = title;
    // The blank document's URL is about:blank, so relative asset URLs
    // (img/app/...) must be resolved against the app instead.
    var base = popupDocument.createElement("base");
    base.href = document.baseURI;
    popupDocument.head.appendChild(base);
    // Emotion styles are injected per window (see PopoutWindow); anything else
    // the app styles itself with (extracted CSS, style-loader tags) is copied.
    document
        .querySelectorAll('link[rel="stylesheet"], style:not([data-emotion])')
        .forEach(function (node) {
        return popupDocument.head.appendChild(popupDocument.importNode(node, true));
    });
    return popup;
};
/**
 * Renders its children into another window through a portal, so they stay in
 * this React tree: the store, contexts and live state are shared with the main
 * window with nothing to synchronise.
 *
 * Emotion injects styles into the document its cache points at, and
 * `@animech/ui` portals tooltips and modals into the roots of the nearest
 * providers, so the popout gets its own cache, baseline styles and roots —
 * otherwise its styles and tooltips would end up in the main window.
 */
export var PopoutWindow = function (_a) {
    var targetWindow = _a.targetWindow, children = _a.children;
    var _b = __read(useState(null), 2), container = _b[0], setContainer = _b[1];
    var cache = useMemo(function () {
        var popoutCache = createCache({
            key: "popout",
            container: targetWindow.document.head,
        });
        // Same as StyleProvider's cache: silences the SSR-only :first-child warning.
        popoutCache.compat = true;
        return popoutCache;
    }, [targetWindow]);
    // The container is attached before anything renders into it, so children's
    // layout effects measure a live element.
    useLayoutEffect(function () {
        var element = targetWindow.document.createElement("div");
        element.id = "popout-root";
        targetWindow.document.body.appendChild(element);
        setContainer(element);
        return function () {
            element.remove();
            setContainer(null);
        };
    }, [targetWindow]);
    if (!container)
        return null;
    return createPortal(_jsxs(CacheProvider, { value: cache, children: [_jsx(StyleBaseline, {}), _jsx(GlobalStyle, {}), _jsx(Styled.PopoutGlobal, {}), _jsx(TooltipProvider, { children: _jsxs(ModalProvider, { children: [children, _jsx(ModalRoot, { zIndex: ZIndex.MODAL }), _jsx(TooltipRoot, { zIndex: ZIndex.TOOLTIP })] }) })] }), container);
};
var fitScale = function (targetWindow, width, height) {
    return Math.min(targetWindow.innerWidth / width, targetWindow.innerHeight / height);
};
/**
 * The uniform scale that fits a `width` × `height` box inside `targetWindow`.
 * Content built from fixed pixel sizes is scaled as a whole (like the
 * dashboard, see useDashboardScale) so its internal alignment holds.
 */
export var useFitToWindowScale = function (targetWindow, width, height) {
    var _a = __read(useState(function () {
        return fitScale(targetWindow, width, height);
    }), 2), scale = _a[0], setScale = _a[1];
    useEffect(function () {
        var recompute = function () { return setScale(fitScale(targetWindow, width, height)); };
        recompute();
        targetWindow.addEventListener("resize", recompute);
        return function () { return targetWindow.removeEventListener("resize", recompute); };
    }, [targetWindow, width, height]);
    return scale;
};
